/**
 * Minimal ID3v2 text-frame parser.
 *
 * Radio HLS streams (including DR's) embed the currently-playing track
 * inline as ID3 tags in MPEG-TS segments. hls.js surfaces these via
 * Hls.Events.FRAG_PARSING_METADATA; Safari surfaces them as <audio>
 * text-track cues. We only need a handful of frame ids — TIT2 (title),
 * TPE1 (artist), TALB (album), TXXX (e.g. StreamTitle) — so a focused
 * parser is way smaller than pulling a whole npm package.
 */

export type Id3Frames = {
  title?: string;
  artist?: string;
  album?: string;
  /** Raw concatenation of "StreamTitle"-style TXXX values */
  streamTitle?: string;
};

function decode(bytes: Uint8Array, encoding: number): string {
  // 0 = ISO-8859-1, 1 = UTF-16 with BOM, 2 = UTF-16BE, 3 = UTF-8
  const enc =
    encoding === 0 ? "iso-8859-1" :
    encoding === 1 ? "utf-16" :
    encoding === 2 ? "utf-16be" :
    "utf-8";
  try {
    return new TextDecoder(enc).decode(bytes).replace(/\0+$/g, "").trim();
  } catch {
    return new TextDecoder("utf-8").decode(bytes).replace(/\0+$/g, "").trim();
  }
}

/** Parse an ID3v2 tag from raw bytes. Returns the frames we care about. */
export function parseId3(buf: Uint8Array): Id3Frames | null {
  // header: "ID3" + version + flags + 4-byte synchsafe size
  if (buf.length < 10) return null;
  if (buf[0] !== 0x49 || buf[1] !== 0x44 || buf[2] !== 0x33) return null;
  const version = buf[3]; // 2, 3, or 4
  const tagSize =
    ((buf[6] & 0x7f) << 21) |
    ((buf[7] & 0x7f) << 14) |
    ((buf[8] & 0x7f) << 7) |
    (buf[9] & 0x7f);
  const end = Math.min(buf.length, 10 + tagSize);

  const out: Id3Frames = {};
  let pos = 10;
  while (pos + 10 < end) {
    const idBytes = buf.subarray(pos, pos + 4);
    // null/zero id = padding, stop
    if (idBytes[0] === 0) break;
    const frameId = String.fromCharCode(...idBytes);
    let frameSize: number;
    if (version >= 4) {
      // synchsafe
      frameSize =
        ((buf[pos + 4] & 0x7f) << 21) |
        ((buf[pos + 5] & 0x7f) << 14) |
        ((buf[pos + 6] & 0x7f) << 7) |
        (buf[pos + 7] & 0x7f);
    } else {
      frameSize =
        (buf[pos + 4] << 24) | (buf[pos + 5] << 16) | (buf[pos + 6] << 8) | buf[pos + 7];
    }
    pos += 10; // past frame header
    if (frameSize <= 0 || pos + frameSize > end) break;

    const frameBytes = buf.subarray(pos, pos + frameSize);

    if (frameId[0] === "T" && frameId !== "TXXX") {
      // Text frame: 1 encoding byte, then text
      const encoding = frameBytes[0];
      const text = decode(frameBytes.subarray(1), encoding);
      if (frameId === "TIT2") out.title = text;
      else if (frameId === "TPE1") out.artist = text;
      else if (frameId === "TALB") out.album = text;
    } else if (frameId === "TXXX") {
      // User text frame: encoding + null-terminated description + value
      const encoding = frameBytes[0];
      const rest = frameBytes.subarray(1);
      // find separator: 0x00 for single-byte encodings, 0x00 0x00 for utf-16
      let sepIdx = -1;
      if (encoding === 0 || encoding === 3) {
        sepIdx = rest.indexOf(0);
      } else {
        for (let i = 0; i + 1 < rest.length; i += 2) {
          if (rest[i] === 0 && rest[i + 1] === 0) { sepIdx = i; break; }
        }
      }
      if (sepIdx >= 0) {
        const desc = decode(rest.subarray(0, sepIdx), encoding);
        const value = decode(rest.subarray(sepIdx + (encoding === 0 || encoding === 3 ? 1 : 2)), encoding);
        if (/streamtitle/i.test(desc)) out.streamTitle = value;
      }
    } else if (frameId === "PRIV") {
      // private frame; skip
    }
    pos += frameSize;
  }
  return out;
}

/** Find and parse the first ID3 tag inside a buffer that may be raw PES data. */
export function findAndParseId3(buf: Uint8Array): Id3Frames | null {
  // Some payloads embed the ID3 tag a few bytes into the buffer.
  for (let i = 0; i < Math.min(buf.length - 10, 256); i++) {
    if (buf[i] === 0x49 && buf[i + 1] === 0x44 && buf[i + 2] === 0x33) {
      const parsed = parseId3(buf.subarray(i));
      if (parsed) return parsed;
    }
  }
  return null;
}

/** Some stations stuff "Artist - Title" into a single field. Split if so. */
export function splitArtistTitle(s: string): { artist?: string; title: string } {
  const m = s.match(/^\s*([^-–]+?)\s+[-–]\s+(.+?)\s*$/);
  if (!m) return { title: s.trim() };
  return { artist: m[1].trim(), title: m[2].trim() };
}
